import { z } from "zod";
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value => {
    const time = Date.parse(`${value}T00:00:00Z`);
    return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === value;
}, "Invalid calendar date");
const count = z.number().int().nonnegative();
const amount = z.number().finite().nullable();
const month = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
const energy = z.number().finite().nonnegative().nullable();
const traderId = z.union([z.string().uuid(), z.literal("unassigned"), z.literal("masked")]);
const bookId = z.union([z.literal("unbooked"), z.string().startsWith("book:").min(6).max(1005)]);
export const EexStatsQuerySchema = z.object({
    from: day.optional(), to: day.optional(), book: bookId.optional(), trader: traderId.optional(),
    commodity: z.enum(["all", "power", "gas"]).default("all"),
    origin: z.enum(["all", "cleared", "bilateral"]).default("all"),
}).strict().superRefine((value, ctx) => {
    if (value.from && value.to && value.from > value.to)
        ctx.addIssue({ code: "custom", path: ["to"], message: "End date must follow start date" });
    if (value.from && value.to && Date.parse(value.to) - Date.parse(value.from) > 3660 * 86400000) {
        ctx.addIssue({ code: "custom", path: ["to"], message: "Choose a date range of at most ten years" });
    }
});
export const EexStatsCoverageSchema = z.object({
    status: z.enum(["available", "incomplete", "unavailable", "restricted"]),
    missingCount: count,
    reason: z.string().nullable(),
});
const coverage = EexStatsCoverageSchema;
export const EexStatsPerformanceSchema = z.object({
    recognizedPnlEUR: amount, estimatedPnlEUR: amount, openMtmEUR: amount, tradingCostsEUR: amount,
    coverage: z.object({ recognizedPnlEUR: coverage, estimatedPnlEUR: coverage, openMtmEUR: coverage, tradingCostsEUR: coverage }),
});
export const EexStatsMetricsSchema = z.object({
    tradeCount: count, clearedCount: count, bilateralCount: count, powerCount: count, gasCount: count, energyTradeCount: count,
    grossMWh: energy, knownMWh: z.number().finite().nonnegative(), buyMWh: energy, sellMWh: energy,
    powerMWh: energy, gasMWh: energy, powerBuyMWh: energy, powerSellMWh: energy, gasBuyMWh: energy, gasSellMWh: energy,
    missingVolumeCount: count, unclassifiedSideCount: count,
    performance: EexStatsPerformanceSchema,
});
// Financial-month observations, not execution cohorts or interpolated samples.
// Null preserves unavailable/restricted financial coverage for a group's month.
export const EexStatsMonthlyPerformanceSchema = z.array(z.object({ month, recognizedPnlEUR: amount }));
export const EexStatsResponseSchema = z.object({
    schemaVersion: z.literal(1), generatedAt: z.string().datetime(), asOf: day,
    filters: z.object({ from: day, to: day, book: bookId.optional(), trader: traderId.optional(), commodity: z.enum(["all", "power", "gas"]), origin: z.enum(["all", "cleared", "bilateral"]) }),
    options: z.object({
        books: z.array(z.object({ id: bookId, label: z.string() })),
        traders: z.array(z.object({ id: traderId, label: z.string(), identityMasked: z.boolean() })),
    }),
    summary: EexStatsMetricsSchema,
    monthly: z.array(EexStatsMetricsSchema.extend({ month })),
    books: z.array(EexStatsMetricsSchema.extend({ id: bookId, book: z.string().nullable(), label: z.string(), monthlyPerformance: EexStatsMonthlyPerformanceSchema })),
    traders: z.array(EexStatsMetricsSchema.extend({ id: traderId, traderId, label: z.string(), identityMasked: z.boolean(), monthlyPerformance: EexStatsMonthlyPerformanceSchema })),
    bookTraders: z.array(EexStatsMetricsSchema.extend({ id: z.string(), book: z.string().nullable(), bookLabel: z.string(), traderId, traderLabel: z.string(), identityMasked: z.boolean(), monthlyPerformance: EexStatsMonthlyPerformanceSchema })),
    allocation: z.array(z.object({ commodity: z.enum(["power", "gas", "unknown"]), book: z.string().nullable(), bookLabel: z.string(), traderId, traderLabel: z.string(), identityMasked: z.boolean(), tradeCount: count, grossMWh: energy, knownMWh: z.number().finite().nonnegative(), missingVolumeCount: count })),
    metadata: z.object({ activityBasis: z.string(), recognitionBasis: z.string(), openMtmBasis: z.string(), performanceScope: z.string(), missingTradeDateCount: count, warnings: z.array(z.string()) }),
});
//# sourceMappingURL=eex-stats-contracts.js.map